import React, { useEffect, useMemo, useState } from 'react';
import { FaBuilding, FaSearch, FaSpinner, FaCheckCircle, FaTimesCircle, FaPhoneAlt, FaEnvelope, FaClock } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { useToast } from '../utils/toastContext';
import { formatApiError } from '../utils/apiErrorFormatter';
import '../styles/ManageCurriculums.css';
import '../styles/ManageRanks.css';

const STATUS_OPTIONS = ['pending', 'contacted', 'approved', 'rejected'];

const STATUS_LABELS = {
  pending: 'Pending',
  contacted: 'Contacted',
  approved: 'Approved',
  rejected: 'Rejected',
};

const OrganizationApplicationsReview = () => {
  const { showSuccess, showError } = useToast();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/organization/applications');
      setApplications(Array.isArray(res.data?.applications) ? res.data.applications : []);
    } catch (err) {
      showError(formatApiError(err, 'Failed to load organization applications'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleStatusChange = async (applicationId, status) => {
    setUpdatingId(applicationId);
    try {
      await axiosInstance.put(`/organization/applications/${applicationId}/status`, { status });
      setApplications((prev) => prev.map((app) => (
        app._id === applicationId ? { ...app, status } : app
      )));
      showSuccess(`Marked as ${STATUS_LABELS[status]}`);
    } catch (err) {
      showError(formatApiError(err, 'Failed to update status'));
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = useMemo(() => applications.filter((app) => {
    if (statusFilter !== 'all' && (app.status || 'pending') !== statusFilter) return false;
    const haystack = `${app.organization_name || ''} ${app.first_name || ''} ${app.last_name || ''} ${app.email || ''} ${app.role || ''}`.toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  }), [applications, statusFilter, searchTerm]);

  const counts = useMemo(() => STATUS_OPTIONS.reduce((acc, status) => {
    acc[status] = applications.filter((app) => (app.status || 'pending') === status).length;
    return acc;
  }, {}), [applications]);

  if (loading) {
    return (
      <div className="mc-page" style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <FaSpinner className="mc-spin" style={{ fontSize: '3rem', color: '#4a90e2', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div className="mc-page">
      <div className="universal-white-container mc-shell">
        <div className="mc-hero">
          <div className="mc-hero__copy">
            <span className="mc-eyebrow">Super Admin</span>
            <h1><FaBuilding /> Organization Applications</h1>
            <p>Institutions that requested to join the platform via the login page.</p>
          </div>
          <div className="mc-hero__stats">
            {STATUS_OPTIONS.map((status) => (
              <div className="mc-stat" key={status}>
                <strong>{counts[status] || 0}</strong>
                <span>{STATUS_LABELS[status]}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mc-toolbar">
          <div className="mc-search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Search by organization, contact name, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="mr-status-filter"
          >
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{STATUS_LABELS[status]}</option>
            ))}
          </select>
        </div>

        <div className="mc-cards-grid">
          {filtered.length === 0 ? (
            <div className="mc-empty-state">
              <FaBuilding />
              <h3>{applications.length === 0 ? 'No applications yet' : 'No matching applications'}</h3>
              <p>{applications.length === 0 ? 'Submissions from the login page will show up here' : 'Try a different search or filter'}</p>
            </div>
          ) : (
            filtered.map((app) => {
              const status = app.status || 'pending';
              return (
                <div key={app._id} className={`mc-curriculum-card mr-card--${status === 'approved' ? 'experience' : status === 'rejected' ? 'institution' : ''}`}>
                  <div className="mc-curriculum-card__top">
                    <div>
                      <h4>{app.organization_name}</h4>
                      {app.organization_type && <p className="mc-curriculum-card__rank">{app.organization_type}</p>}
                    </div>
                    <div className={`mc-status-pill ${status === 'approved' ? 'is-status-active' : status === 'contacted' ? 'is-status-upcoming' : ''}`}>
                      {STATUS_LABELS[status]}
                    </div>
                  </div>

                  <div className="mc-curriculum-card__meta">
                    <span>{app.first_name} {app.last_name} — {app.role}</span>
                    <span><FaEnvelope /> {app.email}</span>
                    {app.phone && <span><FaPhoneAlt /> {app.phone}</span>}
                    {(app.city || app.country) && <span>{[app.city, app.country].filter(Boolean).join(', ')}</span>}
                  </div>

                  {app.message && (
                    <p className="mr-hint" style={{ fontStyle: 'italic', marginTop: '0.6rem' }}>&ldquo;{app.message}&rdquo;</p>
                  )}

                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                    {STATUS_OPTIONS.filter((s) => s !== status).map((nextStatus) => (
                      <button
                        key={nextStatus}
                        type="button"
                        className={nextStatus === 'approved' ? 'mc-secondary-btn' : nextStatus === 'rejected' ? 'mc-danger-btn' : 'mc-ghost-btn'}
                        disabled={updatingId === app._id}
                        onClick={() => handleStatusChange(app._id, nextStatus)}
                      >
                        {nextStatus === 'approved' ? <FaCheckCircle /> : nextStatus === 'rejected' ? <FaTimesCircle /> : <FaClock />}
                        {STATUS_LABELS[nextStatus]}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

export default OrganizationApplicationsReview;
